import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useRef } from 'preact/hooks';
import { Upload, Download } from 'lucide-preact';
import { BRAND_CLASSES } from '@/constants/theme';
export function FileUpload({ label = 'File Upload', required = false, error, helperText = 'Select a file to upload', accept = '.csv,text/csv,application/csv', maxSizeMB = 10, selectedFile, onFileChange, onError, sampleFileUrl, sampleFileName = 'Sample File', containerClassName = '', validateFile }) {
    const fileInputRef = useRef(null);
    const handleFileChange = (e) => {
        const target = e.target;
        const file = target.files?.[0];
        if (!file) {
            onFileChange(null);
            return;
        }
        // Custom validation if provided
        if (validateFile) {
            const validationError = validateFile(file);
            if (validationError) {
                onError?.(validationError);
                target.value = '';
                onFileChange(null);
                return;
            }
        }
        else {
            // Default validation - check file type
            const fileName = file.name.toLowerCase();
            const acceptedExtensions = accept.split(',').map(ext => ext.trim().toLowerCase());
            const hasValidExtension = acceptedExtensions.some(ext => {
                if (ext.startsWith('.')) {
                    return fileName.endsWith(ext);
                }
                return file.type === ext;
            });
            if (!hasValidExtension) {
                const extensions = acceptedExtensions
                    .filter(ext => ext.startsWith('.'))
                    .join(', ');
                onError?.(`Please select a valid file type: ${extensions}`);
                target.value = '';
                onFileChange(null);
                return;
            }
            // Check file size
            const maxSizeBytes = maxSizeMB * 1024 * 1024;
            if (file.size > maxSizeBytes) {
                onError?.(`File size must be less than ${maxSizeMB}MB.`);
                target.value = '';
                onFileChange(null);
                return;
            }
        }
        onFileChange(file);
    };
    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { className: BRAND_CLASSES.INPUT.LABEL, children: [label, required && _jsx("span", { className: BRAND_CLASSES.INPUT.REQUIRED, children: " *" })] })), _jsxs("div", { className: "flex items-center gap-4 mt-2", children: [_jsx("input", { type: "file", ref: fileInputRef, accept: accept, onChange: handleFileChange, className: "hidden" }), _jsxs("button", { type: "button", onClick: handleButtonClick, className: `flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg transition-colors ${selectedFile
                            ? 'border-green-300 bg-green-50 dark:border-green-600 dark:bg-green-900/20'
                            : error
                                ? 'border-red-300 bg-red-50 dark:border-red-600 dark:bg-red-900/20'
                                : 'border-slate-300 dark:border-slate-600 hover:border-primary-400 dark:hover:border-primary-500'}`, children: [_jsx(Upload, { className: "w-5 h-5 text-slate-700 dark:text-white" }), _jsx("span", { className: "text-sm text-slate-700 dark:text-white", children: selectedFile ? `Selected: ${selectedFile.name}` : helperText })] })] }), error && _jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error }), _jsxs("div", { className: "flex items-center justify-between mt-2", children: [_jsxs("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: ["Maximum file size: ", maxSizeMB, "MB"] }), sampleFileUrl && (_jsxs("a", { href: sampleFileUrl, download: sampleFileName, className: "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-md transition-colors", title: `Download ${sampleFileName}`, children: [_jsx(Download, { className: "w-3.5 h-3.5" }), "Download Sample"] }))] })] }));
}
